/**
 * Hoe dringend een wachtende chat is.
 *
 * Het oude dashboard kleurde wachttijden en dat was geen versiering: een
 * chatter ziet aan de kleur of hij nog tijd heeft. Onder een kwartier is
 * groen, tot een uur oranje, daarboven rood. Die kleuren waren bij de
 * overstap weggevallen; hier staan ze één keer, zodat elk paneel, de kopbalk
 * en het chatvenster dezelfde grens hanteren.
 *
 * De drempels zijn instelbaar (Settings → Urgency) omdat ze per agency
 * kunnen verschillen; zonder instelling gelden vijftien en zestig minuten.
 */

export type Urgentie = 'ok' | 'warn' | 'bad'

/** De grenzen in minuten. */
export interface Drempels {
  /** Tot hier is groen. */
  groen: number
  /** Tot hier is oranje; daarboven rood. */
  oranje: number
}

export const STANDAARD_DREMPELS: Drempels = { groen: 15, oranje: 60 }

/** De sleutel waaronder de drempels op de server staan. */
export const DREMPEL_SLEUTEL = 'chat_urgency_thresholds'

let huidig: Drempels = { ...STANDAARD_DREMPELS }

/** De drempels die nu gelden. */
export function drempels(): Drempels {
  return huidig
}

/**
 * Zet de drempels, bijvoorbeeld nadat Settings ze heeft opgehaald.
 *
 * Onzin wordt genegeerd: een oranje-grens onder de groene zou elke rij rood
 * maken, en een ontbrekende waarde valt terug op de standaard.
 */
export function zetDrempels(waarde: unknown): Drempels {
  const v = (waarde ?? {}) as Partial<Drempels>
  const groen = Number(v.groen)
  const oranje = Number(v.oranje)
  huidig = {
    groen: Number.isFinite(groen) && groen > 0 ? groen : STANDAARD_DREMPELS.groen,
    oranje: Number.isFinite(oranje) && oranje > 0 ? oranje : STANDAARD_DREMPELS.oranje,
  }
  if (huidig.oranje <= huidig.groen) huidig = { ...STANDAARD_DREMPELS }
  return huidig
}

/** Hoeveel minuten er is gewacht sinds een tijdstip. */
export function minutenSinds(waarde: unknown): number | null {
  if (!waarde) return null
  const d = new Date(String(waarde))
  const t = d.getTime()
  if (!Number.isFinite(t) || t === 0) return null
  return Math.max(0, (Date.now() - t) / 60000)
}

/** Groen, oranje of rood op basis van de wachttijd in minuten. */
export function urgentieVanMinuten(minuten: number | null | undefined): Urgentie {
  const m = Number(minuten ?? 0)
  const d = drempels()
  if (m <= d.groen) return 'ok'
  if (m <= d.oranje) return 'warn'
  return 'bad'
}

/** Hetzelfde, maar vanaf een tijdstip. */
export function urgentieVan(waarde: unknown): Urgentie {
  const m = minutenSinds(waarde)
  return m === null ? 'ok' : urgentieVanMinuten(m)
}

/**
 * De zwaarste urgentie uit een reeks tijdstippen.
 *
 * De teller boven een paneel neemt die kleur over: staat er één rode rij in,
 * dan is de teller rood. Zo hoef je niet te scrollen om te zien of er iets
 * dringends onder zit.
 */
export function ergsteUrgentie(tijdstippen: unknown[]): Urgentie {
  let ergste: Urgentie = 'ok'
  for (const t of tijdstippen) {
    const u = urgentieVan(t)
    if (u === 'bad') return 'bad'
    if (u === 'warn') ergste = 'warn'
  }
  return ergste
}

/** Het langst wachtende tijdstip uit een reeks, of null. */
export function oudste(tijdstippen: unknown[]): number | null {
  let hoogste: number | null = null
  for (const t of tijdstippen) {
    const m = minutenSinds(t)
    if (m !== null && (hoogste === null || m > hoogste)) hoogste = m
  }
  return hoogste
}

/**
 * "12m", "3h 20m", "2d 4h" — kort, en met de eenheid erbij.
 *
 * De kleur alleen is niet genoeg: wie kleuren slecht onderscheidt moet de
 * tijd zelf kunnen lezen, en dat is ook precies de informatie die telt.
 */
export function wachttijdKort(minuten: number | null | undefined): string {
  const m = Number(minuten ?? 0)
  if (!Number.isFinite(m)) return '—'
  if (m < 60) return `${Math.round(m)}m`
  const uren = Math.floor(m / 60)
  if (uren < 24) return `${uren}h ${Math.round(m % 60)}m`
  return `${Math.floor(uren / 24)}d ${uren % 24}h`
}
